import { GetMoviesDocument, GetShowsDocument } from '@lib/graphql/generated';
import { usePaginatedQuery } from '@lib/graphql/urql';
import { getObjectValue } from '@utils/common';
import type { PropsWithClassProps } from '@vgn-medien-holding/vgn-fe-components';
import { type PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { EntityMixGrid } from './EntityMixGrid';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const interleave = ([x, ...xs]: any = [], ys: any = []) => (x === undefined ? ys : [x, ...interleave(ys, xs)]);

// Stable key for a set of query variables, independent of key order.
const toVariablesKey = (...variables: unknown[]) =>
  JSON.stringify(variables, (_key, value) =>
    value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)))
      : value,
  );

interface EntityMixLegacyProps extends PropsWithChildren, PropsWithClassProps {
  movieVariables: {
    first: number;
    page: number;
    id?: number | string;
    provider?: string[] | [];
    genres?: string[] | [];
    orderBy?: string;
    orderDirection?: string;
  };
  tvshowVariables: {
    first: number;
    page: number;
    id?: number | string;
    provider?: string[] | [];
    genres?: string[] | [];
    orderBy?: string;
    orderDirection?: string;
  };
  movieSkip?: boolean;
  tvshowSkip?: boolean;
}

/**
 * Movie/show grid backed by two separate `movies` / `tvshows` queries, interleaved
 * client-side.
 *
 * Superseded by `EntityMix`, which lets the `entities` query mix and sort both types
 * server-side. The provider page still uses this one because `entities(provider:)`
 * without a `type` filter takes 23-50s upstream (~8s for these two queries in
 * parallel) and 503s behind Varnish. Once that is fixed upstream, move the provider
 * page over to `EntityMix` and delete this file.
 */
export const EntityMixLegacy = ({
  movieVariables,
  tvshowVariables,
  movieSkip,
  tvshowSkip,
  children,
  classProps,
}: EntityMixLegacyProps) => {
  const [
    { data: movieData, fetching: moviesLoading, hasMorePages: hasMoreMoviesPages, fetchNextPage: fetchMoreMovies },
  ] = usePaginatedQuery({
    query: GetMoviesDocument,
    variables: movieVariables,
    pause: movieSkip,
  });
  const hasMoreMovies = hasMoreMoviesPages && !movieSkip;
  const movies = movieData?.flatMap((data) => getObjectValue(data, ['movies'])?.data ?? []);

  const [{ data: tvshowData, fetching: showsLoading, hasMorePages: hasMoreShowsPages, fetchNextPage: fetchMoreShows }] =
    usePaginatedQuery({
      query: GetShowsDocument,
      variables: tvshowVariables,
      pause: tvshowSkip,
    });
  const hasMoreShows = hasMoreShowsPages && !tvshowSkip;
  const tvshows = tvshowData?.flatMap((data) => getObjectValue(data, ['tvshows'])?.data ?? []);

  // The paginated hooks keep the pages they already loaded while the next one is
  // in flight. That is what should stay on screen for "Mehr laden", but after a
  // filter or sort change those pages belong to the previous query, so they are
  // dropped until the hooks have reset to the new variables.
  const variablesKey = toVariablesKey(movieVariables, tvshowVariables);
  const [appliedVariablesKey, setAppliedVariablesKey] = useState(variablesKey);
  const hasStalePages = appliedVariablesKey !== variablesKey;

  useEffect(() => {
    setAppliedVariablesKey(variablesKey);
  }, [variablesKey]);

  const entities = useMemo(() => {
    if (hasStalePages) return [];
    const localMovies = movies?.length > 0 && !movieSkip ? movies : [];
    const localShows = tvshows?.length > 0 && !tvshowSkip ? tvshows : [];
    return interleave(localMovies, localShows);
  }, [hasStalePages, movies, tvshows, movieSkip, tvshowSkip]);

  return (
    <EntityMixGrid
      entities={entities}
      fetching={moviesLoading || showsLoading}
      hasMorePages={hasMoreShows || hasMoreMovies}
      onLoadMore={() => {
        hasMoreMovies && fetchMoreMovies();
        hasMoreShows && fetchMoreShows();
      }}
      classProps={classProps}
    >
      {children}
    </EntityMixGrid>
  );
};
